<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useHeaderPins,
  CASAS,
  MODULOS,
} from 'dashboard/composables/useHeaderPins';

/**
 * Personalizar cabeçalho: as três casas de cada módulo, numa tela só.
 *
 * O caminho rápido (fixar direto no popover do módulo) resolve o dia a dia;
 * este painel é para quem quer ver tudo junto e decidir de uma vez. Mesma
 * preferência, mesmo composable: aqui não existe estado próprio.
 */
const emit = defineEmits(['close']);

const { t } = useI18n();
const { casaDe, moverPara } = useHeaderPins();

const ROTULOS = {
  ai: 'CONVERSATION.HEADER.PINS.MODULE_AI',
  agent: 'CONVERSATION.HEADER.PINS.MODULE_AGENT',
  funnel: 'CONVERSATION.HEADER.PINS.MODULE_FUNNEL',
  tasks: 'CONVERSATION.HEADER.PINS.MODULE_TASKS',
  agenda: 'CONVERSATION.HEADER.PINS.MODULE_AGENDA',
};

const OPCOES = [
  { casa: CASAS.TOPO, rotulo: 'CONVERSATION.HEADER.PINS.HOME_TOP' },
  { casa: CASAS.BARRA, rotulo: 'CONVERSATION.HEADER.PINS.HOME_RAIL' },
  { casa: CASAS.MENU, rotulo: 'CONVERSATION.HEADER.PINS.HOME_MENU' },
];

const linhas = computed(() =>
  MODULOS.map(modulo => ({
    modulo,
    rotulo: t(ROTULOS[modulo]),
    casa: casaDe(modulo),
  }))
);

// Sem teto (01/09): qualquer casa e sempre livre. O cabecalho decide o que cabe.
const escolher = (modulo, casa) => moverPara(modulo, casa);

const onKeydown = event => {
  if (event.key === 'Escape') emit('close');
};
</script>

<template>
  <div
    class="w-[290px] rounded-2xl border border-n-weak bg-n-solid-1 p-2.5 shadow-xl"
    data-testid="header-pin-settings"
    role="dialog"
    :aria-label="t('CONVERSATION.HEADER.PINS.TITLE')"
    @keydown="onKeydown"
  >
    <p
      class="px-1.5 pb-2 text-[11px] font-semibold uppercase tracking-wider text-n-slate-11"
    >
      {{ t('CONVERSATION.HEADER.PINS.TITLE') }}
    </p>

    <div
      v-for="linha in linhas"
      :key="linha.modulo"
      class="flex min-h-11 items-center gap-2 rounded-xl px-1.5 hover:bg-n-alpha-1"
      :data-testid="`pin-row-${linha.modulo}`"
    >
      <span class="min-w-0 flex-1 truncate text-[13px] text-n-slate-12">
        {{ linha.rotulo }}
      </span>
      <span
        class="flex shrink-0 overflow-hidden rounded-full border border-n-weak"
        role="radiogroup"
        :aria-label="linha.rotulo"
      >
        <button
          v-for="opcao in OPCOES"
          :key="opcao.casa"
          type="button"
          role="radio"
          :aria-checked="linha.casa === opcao.casa"
          :data-testid="`pin-${linha.modulo}-${opcao.casa}`"
          class="px-2.5 py-1.5 text-[10.5px] font-semibold disabled:opacity-40"
          :class="
            linha.casa === opcao.casa
              ? 'bg-n-blue-9 text-white'
              : 'text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="escolher(linha.modulo, opcao.casa)"
        >
          {{ t(opcao.rotulo) }}
        </button>
      </span>
    </div>
  </div>
</template>
